'use client';

import { type BlocksContent } from '@strapi/blocks-react-renderer';
import clsx from 'clsx';
import { FC, useEffect, useRef } from 'react';

import { Typography, TypographyProps } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import cn from '@/helpers/cn';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	title: BlocksContent;
	url: string;
	className?: string;
	typography?: Omit<TypographyProps, 'variant' | 'children'> & {
		variant?: TypographyProps['variant'];
	};
	isIndustry?: boolean;
}

const TitleGraffiti: FC<Props> = ({ title, url, className, typography, isIndustry }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);

	const shouldLoadVideo = useLazyVideoSource(wrapperRef);
	useViewportVideosPlayback([wrapperRef], { scrollMargin: '250px 0px 250px 0px' });

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const title = wrapper?.querySelector(`.${css.title}`);
		const titleLines = wrapper?.querySelectorAll(`.${css.title} p`);

		if (!wrapper || !title || !titleLines) return;

		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: `(min-width: 992px)`,
					isMobile: `(max-width: 991px)`
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (isMobile) return;

					gsap.to(title, {
						yPercent: 90,
						force3D: !isMobile,
						willChange: 'transform',
						scrollTrigger: {
							toggleActions: 'play pause pause reverse',
							trigger: wrapper,
							start: 'top bottom',
							end: 'center center',
							scrub: true
						}
					});

					gsap.set(titleLines, {
						opacity: 0,
						y: -40,
						filter: 'blur(10px)'
					});

					gsap.to(titleLines, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: 0.15,
						scrollTrigger: {
							trigger: titleLines,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});
				}
			);
		}, wrapper);

		return () => ctx.revert();
	}, []);

	return (
		<div ref={wrapperRef} className={cn(css.wrapper, className ?? '')}>
			{title ? (
				<Typography
					uppercase
					variant={isIndustry ? 'h2-industry' : 'h2'}
					tag="h2"
					{...typography}
					className={clsx(css.title, typography?.className)}
				>
					<StrapiRichContent content={title} />
				</Typography>
			) : null}

			<video loop muted playsInline className={css.graffiti} controls={false} preload="none">
				{shouldLoadVideo ? <source src={url} type="video/mp4" /> : null}
				Your browser does not support the video tag.
			</video>
		</div>
	);
};

export default TitleGraffiti;
